import { AloftPrice, isDefined, isUndefined } from '@aloft/toolkit-shared';

import { Typography } from '../../../summary/components/Typography.tsx';
import { isCosted } from '../AdjustmentBreakdown/isCosted.ts';
import { SummarySection } from '../SummarySection.tsx';
import { SummaryTableHorizontal } from '../SummaryTableHorizontal.tsx';
import { TitleAndExportToggleLockup } from '../TitleAndExportToggleLockup.tsx';
import { BreakdownColumn, Tkv2PairedCostSummaryInterfaceV2 } from '../types.ts';
import { adjustment } from '../utils/formatters/adjustment.ts';
import { getTableData } from '../utils/getTableData.ts';

const tableDef: BreakdownColumn[] = [
  {
    header: 'SF | Finished SF | QL',
    columns: [
      { type: 'feature', adjustmentType: 'basement', path: 'sqft' },
      { type: 'feature', adjustmentType: 'basement', path: 'finishedSqft' },
      { type: 'feature', adjustmentType: 'basement', path: 'dcQuality' },
    ],
    combiner: ([sqft, finishedSqft, dcQuality]) => {
      if (isUndefined(sqft) && isUndefined(finishedSqft) && isUndefined(dcQuality)) {
        return '';
      }
      return `${isDefined(sqft) ? sqft : '-'} | ${
        isDefined(finishedSqft) ? finishedSqft : '-'
      } | ${isDefined(dcQuality) ? dcQuality : '-'}`;
    },
  },
  {
    header: 'Cost New',
    type: 'feature',
    adjustmentType: 'basement',
    path: 'costNew',
  },
  {
    header: '- Dep',
    type: 'feature',
    adjustmentType: 'basement',
    path: 'depreciation',
  },
  {
    header: '= Dep Cost',
    type: 'feature',
    adjustmentType: 'basement',
    path: 'depreciatedCost',
  },
  {
    header: '= Adjustment',
    type: 'adjustment',
    adjustmentType: 'basement',
    path: 'adjustment',
    valueFormatter: adjustment,
  },
];

export const AdjustmentBreakdownDepreciatedCostAdditionalImprovementsBasement = (
  props: Tkv2PairedCostSummaryInterfaceV2,
) => {
  if (!isCosted(props.results, 'basement')) {
    return null;
  }

  const tableData = getTableData(tableDef, props, props.compSequence);

  const sortedPrices = Object.values(tableData)
    .map((row) => row[3])
    .filter(AloftPrice.isAloftPrice)
    .sort((a, b) => (a as AloftPrice).compare(b as AloftPrice));

  const minString = `${sortedPrices[0]?.toString(true)}`;
  const maxString = `${sortedPrices[sortedPrices.length - 1]?.toString(true)}`;
  const rangeString = [minString, maxString].join(' to ');

  return (
    <SummarySection marginBottom="2rem">
      <TitleAndExportToggleLockup
        {...props}
        title="Basement"
        id="isDepreciatedCostAdditionalImprovementsInExport"
        variant="h3"
      />
      <Typography variant="body1">
        {`Basement adjustments are based on the difference in depreciated cost between the
                subject and comparable. Values ranged from ${rangeString}.`}
      </Typography>
      <SummaryTableHorizontal
        {...props}
        summary={props}
        compSequence={props.compSequence}
        columns={tableDef}
        tableData={tableData}
        subjectHeader="SUB"
        compHeaderPrefix="CP"
      />
    </SummarySection>
  );
};
